import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';

import { CommonStyles } from '../../../../common/react/CommonStyles';
import { CellWithCountAndToolTip } from '../../../../common/react/components/dataGrid/CellWithCount';
import { ContentClickedEventHandler } from '../../../../common/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '../../../../common/telemetryEvents/util/EventHandlerFactories';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { telemetryConsumerWrapper } from '../../../contexts/TelemetryConsumerWrapper';
import {
  CardPositionContext,
  ContentClickedTelemetryContext,
  ContentInfoContext
} from '../../../contexts/TelemetryContexts';
import { ExplorerStyles } from '../../Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../../SpacesSiteUrls';
import { ConnectionToolTip } from '../../Tooltips/ConnectionTooltip';
import { NameToolTip } from '../../Tooltips/NameToolTip';
import { TooltipAnchor } from '../../Tooltips/TooltipAnchor';
import { StyledCellFormatter } from '../StyledCellWrapper';
import { CommonColumn } from '../types/Columns';

export interface ILink {
  text: string;
  column?: CommonColumn;
  title?: string;
  url: string;
  testId?: string;
  style?: any;
  hideTooltips?: boolean;
  iconClassName?: string;
  iconTitle?: string;
  iconUrl?: string;
  openLinkInNewtab?: boolean;
  // This is here so that we don't have multiple layers of factories which doesn't play nice with data grid 3.
  // The bug we are working around is having to click twice on a link for the link to actually receive the click event.
  displayJustText?: boolean;
  isAccessDenied?: boolean;
}

export function getLinkMarkup(link: ILink, row: DataGrid.Row, labelId?: string): JSX.Element {
  let index = row.data.rowIndex;
  const textEllipsis = CommonStyles.Layout.TextEllipsis;
  if (link.column && link.column === CommonColumn.Name) {
    return (
        <ContentInfoContext.Provider value={row.data as IContentInfo}>
          <CardPositionContext.Provider value={index + 1}>
            {telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) =>
                <a href={link.url}
                   target={link.openLinkInNewtab ? '_blank' : null}
                   rel="noopener noreferrer"
                   onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
                   title={link.hideTooltips || link.isAccessDenied ? null : (link.title || link.text)}
                   style={_.extend({display: 'block'}, textEllipsis, link.style)}
                   tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
                   aria-labelledby={labelId}
                >
                  {link.text}
                </a>
            )
            }
          </CardPositionContext.Provider>
        </ContentInfoContext.Provider>
    );
  } else {
    return (
        <a href={link.url}
           title={link.hideTooltips ? null : (link.title || link.text)}
           style={_.extend({display: 'block'}, textEllipsis, link.style)}
           tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
        >
          {link.text}
        </a>
    );
  }
}

export function LinkCell(linkFn: (row: DataGrid.Row) => ILink, tooltip?: boolean, siteUrl?: SpacesSiteUrls): React.Factory<BodyCell.Props> {
  return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        textEllipsis = CommonStyles.Layout.TextEllipsis;

        render() {
          const datasource = this.props.row.data as IDataSourceInfo;
          const spaceUrls = siteUrl;
          const link: ILink = linkFn(this.props.row);
          if (!link || link.isAccessDenied) {
            return null;
          }

          let Icon = null;
          if (link.iconClassName || link.iconTitle) {
            Icon = () => <span data-tb-test-id="data-grid-cell-icon"
                               className={link.iconClassName}
                               title={link.hideTooltips ? null : link.iconTitle}
                               style={{flex: 'none'}}/>;
          }

          return (
              <span data-tb-test-id={link.testId}
                    style={CommonStyles.Layout.FlexBlockLeftAndCenter}>
          {Icon && <Icon/>}
                {link.displayJustText &&
                <span
                    title={(tooltip || link.hideTooltips) ? null : (link.title || link.text)}
                    style={_.extend({display: 'block'}, this.textEllipsis, link.style)}>
                  {tooltip && <TooltipAnchor tooltipContent={() => <NameToolTip content = {this.props.row.data as IDataSourceInfo}
                urls = {spaceUrls}/>}
                disableAutoFocus={true}
                title={Messages.datasourceDetails()}
                disableTooltip={false}>
                  {link.text}
                  </TooltipAnchor>}
                {!tooltip && link.text }
              </span>
                }
                {!link.displayJustText &&
                getLinkMarkup(link, this.props.row)
                }
          </span>
          );
        }
      })
  );
}

export interface LinkWithCountProps extends BodyCell.Props {
  linkFn: (row: DataGrid.Row) => ILink;
  count?: string;
  getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
}

export const LinkWithCount = (props: LinkWithCountProps, siteUrl: SpacesSiteUrls): JSX.Element => {
  const link = props.linkFn(props.row);
  if (!link) {
    return null;
  }

  const textStyles = _.extend({display: 'block'}, CommonStyles.Layout.TextEllipsis, link.style);

  let icon: JSX.Element | undefined = undefined;

  if (link.iconUrl || link.iconTitle) {
    /* eslint-disable jsx-a11y/alt-text */
    icon = <img data-tb-test-id="data-grid-cell-icon"
                style={ExplorerStyles.connectsToListViewIconStyle}
                src={link.iconUrl}
                title={link.iconTitle}/>;
  }

  const datasource = props.row.data as IDataSourceInfo;
  const spaceSiteUrl = new SpacesSiteUrls({ siteUrlName: datasource.urlName});

  const linkEl: JSX.Element = (
      <a href={link.url}
         title={link.hideTooltips ? null : (link.title || link.text)}
         tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
         style={textStyles}>
         {<TooltipAnchor tooltipContent={() => <ConnectionToolTip content = {props.row.data as IDataSourceInfo}
                urls = {spaceSiteUrl}/>}
                disableAutoFocus={true}
                title={Messages.datasourceDetails()}
                disableTooltip={false}>
        {link.text}
        </TooltipAnchor>}
      </a>
  );

  const getContent = () => props.getPopoverContent(props);
  return (
      <CellWithCountAndToolTip count={props.count} testId={link.testId} getPopoverContent={getContent}>
        {icon}
        {linkEl}
      </CellWithCountAndToolTip>
  );
};
